import { AuthProfile, OrgContext } from './auth-profile.model';
import {
  DataPermission,
  isAuthorizedUser,
  USER_ROLES,
  UserContext,
  UserRole,
} from './user-context.model';

/** Least-privileged role — used when the API sends a role string we don't know. */
const FALLBACK_ROLE = UserRole.AuthorizedNonPrivilegeUser;

/** The API role string as a CSD role; an unknown value fails closed to the
 *  least-privileged role (never grants POC / NABP access). */
export function toUserRole(role: string | null | undefined): UserRole {
  return (USER_ROLES as readonly string[]).includes(role ?? '')
    ? (role as UserRole)
    : FALLBACK_ROLE;
}

/**
 * Build the shell's UserContext from the real auth profile (GET /auth/me) and the
 * active org's context (POST /auth/switch-org). Display name comes from the OIDC
 * token (the profile carries no name). API ids are integers → stringified here to
 * match the model consumed by the nav, guards and org switcher.
 *
 * Role, data types, locations and `locationManagementEnabled` are per org: they come
 * from the active org's /auth/me `authorized_orgs` entry, falling back to the
 * switch-org response (role has no fallback — switch-org doesn't carry it). An unknown
 * Location Management value is treated as OFF (fail closed — location features stay
 * hidden).
 *
 * `accessRestricted`: an Authorized User with no data type in the active org, or no
 * location when the org has Location Management enabled (with it OFF, locations are
 * not checked) — the shell then offers only their own Session Audit Log and shows the
 * access banner.
 */
export function toUserContext(
  profile: AuthProfile,
  org: OrgContext,
  displayName: string,
): UserContext {
  const active = profile.authorizedOrgs.find((o) => o.orgId === org.orgId);
  const role = toUserRole(active?.role);
  const dataTypes = (active?.dataTypes ?? org.dataTypes ?? []) as DataPermission[];
  const locationIds = active?.locationIds ?? org.locationIds ?? [];
  const locationManagementEnabled =
    active?.locationManagementEnabled ?? org.locationManagementEnabled ?? false;

  return {
    userId: String(profile.userId),
    displayName,
    role,
    organizationId: String(org.orgId),
    organizationName: org.orgName,
    availableOrganizations: profile.authorizedOrgs.map((o) => ({
      id: String(o.orgId),
      name: o.orgName,
    })),
    assignedDataTypes: dataTypes,
    assignedLocationIds: locationIds.map(String),
    locationManagementEnabled,
    accessRestricted:
      isAuthorizedUser(role) &&
      (dataTypes.length === 0 || (locationManagementEnabled && locationIds.length === 0)),
    // Profession sub-tabs are not part of the profile payload today.
    activeModules: [],
  };
}
